import React, { useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useForm, useFormState, useWatch } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Link } from 'react-router-dom';
import { ReactComponent as CheckIcon } from 'assets/icons/check.svg';

import AuthBackground from 'components/AuthBackground';
import Button from 'components/Button';
import Input from 'components/Input';
import { forgotPassword as TEXT } from 'locales/en';
import { forgotPassword, setResetPasswordEmail } from 'redux/slices/auth';

import PATHS from 'routes/paths';
import schema from './schema';
import emailURL from 'utils/emailURL';

const ForgotPassword = () => {
  const dispatch = useDispatch();
  const defaultEmail = useSelector((state) => state.auth.user.email);
  const { register, handleSubmit, control } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      email: defaultEmail,
    },
  });
  const [isSuccess, setIsSuccess] = useState(false);
  const email = useWatch({
    control,
    name: 'email',
    defaultValue: defaultEmail,
  });
  const { errors, isSubmitting } = useFormState({
    control,
  });

  const onSubmit = async ({ email }) => {
    const action = await dispatch(forgotPassword(email));
    if (action.type.endsWith('fulfilled')) {
      dispatch(setResetPasswordEmail(email));
      setIsSuccess(true);
    }
  };

  const emailLink = useMemo(() => emailURL(email), [email]);

  return isSuccess ? (
    <AuthBackground
      header1={
        <>
          <CheckIcon className="mr-2 inline-block w-8 h-8" />
          <span className="align-middle">{TEXT.successHeader1}</span>
        </>
      }
      header2={TEXT.successHeader2(email)}
    >
      <div className="flex justify-end mt-2">
        {emailLink ? (
          <a
            href={emailLink}
            target="_blank"
            rel="noreferrer"
            className="btn bg-javelin-blue-dark w-44 py-4 font-bold text-base rounded-full mt-2 px-4 text-center"
          >
            {TEXT.goToEmail}
          </a>
        ) : (
          <Link
            to={PATHS.login}
            className="text-sm text-javelin-blue-dark border-0 text-left focus:outline-none"
          >
            {TEXT.return}
          </Link>
        )}
      </div>
    </AuthBackground>
  ) : (
    <AuthBackground header1={TEXT.header1} header2={TEXT.header2}>
      <form
        noValidate
        onSubmit={handleSubmit(onSubmit)}
        className="flex flex-col justify-between"
      >
        <div>
          <Input
            label={TEXT.email}
            name="email"
            type="email"
            register={register}
            required
            error={errors.email?.message}
          />
        </div>
        <div className="flex justify-between items-center mt-2">
          <Link
            to={PATHS.login}
            className="text-sm text-javelin-blue-dark border-0 text-left focus:outline-none"
          >
            {TEXT.login}
          </Link>
          <Button
            className="bg-javelin-blue-dark w-44 py-4 font-bold text-base rounded-full mt-2 col-start-2 md:col-start-3 px-4"
            type="submit"
            title={TEXT.submit}
            showSpinner={isSubmitting}
          />
        </div>
      </form>
    </AuthBackground>
  );
};

export default ForgotPassword;
